import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useState  } from 'react';
import type {FormEvent} from 'react';
import { Input } from '@/components/ui/field';
import { Alert } from '@/components/ui/primitives';
import { dashlite } from '@/lib/dashlite';
import { fieldErrors, presentError } from '@/lib/errors';

/**
 * Inscription à la newsletter — `POST /newsletter/subscribe`.
 *
 * Deux comportements de l'API à connaître avant d'écrire cet écran :
 *
 * 1. **La réponse est la même que l'adresse soit déjà inscrite ou non.** Un
 *    formulaire public n'a pas à révéler qui figure dans la liste. On affiche
 *    donc une confirmation neutre — surtout pas « vous êtes déjà inscrit ».
 *
 * 2. **Un désabonnement antérieur est respecté.** Une adresse qui s'est
 *    désinscrite n'est jamais réabonnée en silence, y compris par l'inscription
 *    automatique qui suit un achat.
 *
 * Le prénom et le nom sont facultatifs : ils servent à personnaliser les envois
 * quand le site a configuré une séquence de messages.
 */
export function NewsletterForm({
    variant = 'inline',
    className,
}: {
    /** `inline` : une seule ligne (pied de page). `full` : formulaire détaillé. */
    variant?: 'inline' | 'full';
    className?: string;
}) {
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const fields = fieldErrors(error);

    async function handleSubmit(event: FormEvent): Promise<void> {
        event.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            await dashlite.subscribeNewsletter({
                email,
                // Les champs vides ne sont pas envoyés : l'API les valide s'ils
                // sont présents, autant ne pas les inventer.
                ...(firstName.trim() ? { first_name: firstName.trim() } : {}),
                ...(lastName.trim() ? { last_name: lastName.trim() } : {}),
            });

            setDone(true);
            setEmail('');
            setFirstName('');
            setLastName('');
        } catch (caught) {
            setError(caught);
        } finally {
            setSubmitting(false);
        }
    }

    if (done) {
        return (
            <Alert tone="positive" title="C'est noté" className={className}>
                Si cette adresse n'était pas déjà inscrite, elle recevra désormais nos actualités. Chaque message
                contient un lien de désinscription en un clic.
            </Alert>
        );
    }

    return (
        <form onSubmit={handleSubmit} className={cn('space-y-3', className)} noValidate>
            {variant === 'full' && (
                <div className="grid gap-3 sm:grid-cols-2">
                    <Input
                        label="Prénom"
                        optional
                        autoComplete="given-name"
                        value={firstName}
                        error={fields.first_name}
                        onChange={(event) => setFirstName(event.target.value)}
                    />
                    <Input
                        label="Nom"
                        optional
                        autoComplete="family-name"
                        value={lastName}
                        error={fields.last_name}
                        onChange={(event) => setLastName(event.target.value)}
                    />
                </div>
            )}

            <div className={cn(variant === 'inline' && 'flex flex-col gap-2 sm:flex-row sm:items-start')}>
                <Input
                    data-dashlite="newsletter-email"
                    label="Adresse email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="vous@exemple.fr"
                    value={email}
                    error={fields.email}
                    onChange={(event) => setEmail(event.target.value)}
                    wrapperClassName={cn(variant === 'inline' && 'flex-1')}
                />

                <Button
                    data-dashlite="newsletter-submit"
                    type="submit"
                    loading={submitting}
                    className={cn(variant === 'inline' && 'sm:mt-7')}
                >
                    S'inscrire
                </Button>
            </div>

            {error !== null && Object.keys(fields).length === 0 && (
                <Alert tone="critical">{presentError(error).message}</Alert>
            )}
        </form>
    );
}
